import Head from "next/head";
import { useTranslation } from "next-i18next";
import React from "react";

import useUrl from "@/hooks/useUrl";

export type MetaOgProps = {
  title: string;
  titleSuffix?: string;
  description?: string;
  ogImage?: string;
  translations?: { language: string; path: string }[];
};

export const MetaOg = ({
  title,
  titleSuffix = " | På(fyll)™",
  description,
  ogImage,
  translations,
}: MetaOgProps) => {
  const { baseUrl, generateHreflangUrls, currentUrl } = useUrl();
  const { t } = useTranslation();

  const metaTitle = `${title}${titleSuffix}`;
  const metaDescription = description || `${t("payoff_1")}${t("payoff_2")}`;
  const metaImage = ogImage ?? `${baseUrl}/assets/ogImage.jpg`;

  const alternateLinks = translations
    ? translations.map((translation) => {
        return (
          <link
            key={translation.language}
            rel="alternate"
            hrefLang={translation.language}
            href={`${baseUrl}/${translation.path}`}
          />
        );
      })
    : generateHreflangUrls().map(({ lang, url }) => (
        <link key={lang} rel="alternate" hrefLang={lang} href={url} />
      ));

  return (
    <Head>
      {/* BASIC META */}
      <title>{metaTitle}</title>
      <meta name="description" content={metaDescription} />
      <link rel="canonical" href={currentUrl} />
      <link rel="shortcut icon" href="/favicon.ico" />
      {/* TWITTER  */}
      <meta name="twitter:title" content={metaTitle} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={metaImage} />
      <meta name="twitter:site" content="@pafyll" />
      {/* OG */}
      <meta property="og:title" content={metaTitle} />
      <meta property="og:url" content={currentUrl} />
      <meta property="og:image" content={metaImage} />
      <meta property="og:description" content={metaDescription} />
      {/* Hreflang tags */}
      {alternateLinks}
    </Head>
  );
};
